import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/components/ui/use-toast";
import { Checkbox } from "@/components/ui/checkbox";
import { validateInvitationToken, setupPassword } from "@/services/auth";
import { trackPasswordSetupCompleted } from "@/analytics/analytics";
import AuthLayout from "@/components/AuthLayout";
import { SITE_URL } from "@/constants/domains";

const SetupPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") || "";

  const [email, setEmail] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenError, setTokenError] = useState("");
  const [tosAccepted, setTosAccepted] = useState(false);
  const [setupSuccess, setSetupSuccess] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    async function validateToken() {
      if (!token) {
        setTokenError("No invitation token provided");
        setValidating(false);
        return;
      }

      try {
        const result = await validateInvitationToken(token);
        setEmail(result.email);
        setCustomerName(result.customerName);
        setTokenValid(true);
      } catch (err: any) {
        setTokenError(err?.message || "Invalid or expired invitation link");
      } finally {
        setValidating(false);
      }
    }

    validateToken();
  }, [token]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (!newPassword || !confirmPassword) {
      toast({
        title: "Missing fields",
        description: "Please fill in all fields.",
        variant: "destructive",
      });
      return;
    }

    if (newPassword.length <= 10) {
      toast({
        title: "Password too short",
        description: "Password must be more than 10 characters.",
        variant: "destructive",
      });
      return;
    }

    if (newPassword !== confirmPassword) {
      toast({
        title: "Passwords do not match",
        description: "Please make sure both passwords match.",
        variant: "destructive",
      });
      return;
    }

    try {
      setLoading(true);
      await setupPassword(token, newPassword, tosAccepted);
      trackPasswordSetupCompleted();
      setSetupSuccess(true);
      toast({
        title: "Success!",
        description: "Your password has been set successfully.",
      });

      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate("/");
      }, 3000);
    } catch (err: any) {
      toast({
        title: "Setup failed",
        description: err?.message || "Failed to setup password. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }

  if (validating) {
    return (
      <AuthLayout>
        <Card className="w-full border-0 bg-transparent shadow-none">
          <CardContent className="pt-6 text-center">
            <p className="text-muted-foreground">Validating invitation link...</p>
          </CardContent>
        </Card>
      </AuthLayout>
    );
  }

  if (!tokenValid) {
    return (
      <AuthLayout>
        <Card className="w-full border-0 bg-transparent shadow-none">
          <CardHeader>
            <CardTitle>Invalid invitation link</CardTitle>
            <CardDescription>This invitation link is not valid</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert variant="destructive">
              <AlertDescription>
                {tokenError || "This invitation link is invalid or has expired."}
              </AlertDescription>
            </Alert>
            <div className="text-sm text-muted-foreground">
              <p>Invitation links expire after 7 days and can only be used once.</p>
            </div>
            <div className="space-y-2 pt-2">
              <Link to="/">
                <Button variant="default" className="w-full">
                  Back to sign in
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </AuthLayout>
    );
  }

  if (setupSuccess) {
    return (
      <AuthLayout>
        <Card className="w-full border-0 bg-transparent shadow-none">
          <CardHeader>
            <CardTitle>Password setup complete</CardTitle>
            <CardDescription>Your password has been set</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert>
              <AlertDescription>
                Your password has been successfully set. You can now sign in with your new password.
              </AlertDescription>
            </Alert>
            <div className="pt-2">
              <Link to="/">
                <Button className="w-full">Continue to sign in</Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout subtitle="One last step.">
      <Card className="w-full border-0 bg-transparent shadow-none">
        <CardHeader>
          <CardDescription>
            Set a password for {email} and your personalized matches are inside.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-1 block">New password</label>
              <Input
                type="password"
                name="new-password"
                autoComplete="new-password"
                placeholder="Enter new password (min 11 characters)"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                autoFocus
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Confirm password</label>
              <Input
                type="password"
                name="confirm-password"
                autoComplete="new-password"
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
              />
            </div>
            <div className="text-xs text-muted-foreground">
              Password must be more than 10 characters long.
            </div>
            <div className="flex items-start space-x-2">
              <Checkbox
                id="tos"
                checked={tosAccepted}
                onCheckedChange={checked => setTosAccepted(checked === true)}
              />
              <label htmlFor="tos" className="text-sm leading-tight cursor-pointer">
                I agree to the{" "}
                <a
                  href={`${SITE_URL}/terms-of-service`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline text-primary-text hover:text-primary-text/80"
                >
                  Terms of Service
                </a>
              </label>
            </div>
            <Button type="submit" className="w-full" disabled={loading || !tosAccepted}>
              {loading ? "Creating account..." : "Create account"}
            </Button>
            <div className="text-center pt-2">
              <Link to="/">
                <Button variant="link" className="px-0 h-auto text-sm">
                  Back to sign in
                </Button>
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </AuthLayout>
  );
};

export default SetupPassword;
